import { useState, useEffect } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getBalance } from '../../services/ExchangeService';
import BuyWizard from './BuyWizard';
import SellScreen from './SellScreen';
import './EasyTrade.css';

// Moedas permitidas (símbolo: nome de exibição)
const ALLOWED_COINS = [
    { symbol: 'BTC', name: 'BTC' },
    { symbol: 'SOL', name: 'SOL' },
    { symbol: 'ETH', name: 'ETH' },
    { symbol: 'USDC', name: 'USDC' },
    { symbol: 'USDT', name: 'USDT' },
    { symbol: 'XRP', name: 'XRP' },
    { symbol: 'BNB', name: 'BNB' },
    { symbol: 'HYPE', name: 'Hyper Liquid' }
];

// Mapeamento de símbolos para WebSocket (formato Binance)
const SYMBOL_MAP = {
    'BTC': 'BTCUSDT',
    'SOL': 'SOLUSDT',
    'ETH': 'ETHUSDT',
    'USDC': 'USDCUSDT',
    'USDT': 'USDTUSDT',
    'XRP': 'XRPUSDT',
    'BNB': 'BNBUSDT',
    'HYPE': 'HYPEUSDT' // Assumindo que Hyper Liquid é HYPE
};

function parseBalanceQty(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'object')
        return parseFloat(value.available ?? value.availableBalance ?? value.free ?? value.current ?? 0) || 0;
    return parseFloat(value) || 0;
}

function CoinCard({ coin, coinName, price, userBalance, usdtBrlRate, onBuy, onSell }) {
    const available = userBalance[coin] ? parseBalanceQty(userBalance[coin].available) : 0;
    const onOrder = userBalance[coin] ? parseBalanceQty(userBalance[coin].onOrder) : 0;
    const hasBalance = available > 0;
    const balance = available;
    const brlValue = usdtBrlRate > 0 ? balance * price * usdtBrlRate : 0;

    return (
        <div className="easytrade-coin-card">
            <div className="easytrade-coin-header">
                <div className="easytrade-coin-symbol">{coinName || coin}</div>
                <div className="easytrade-coin-price">
                    ${parseFloat(price || 0).toLocaleString('pt-BR', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: coin === 'USDT' || coin === 'USDC' ? 2 : 8 
                    })}
                </div>
            </div>
            {hasBalance && (
                <div className="easytrade-coin-balance">
                    Disponível: {balance.toFixed(8)} {coin}
                    {brlValue > 0 && (
                        <span> · R$ {brlValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    )}
                </div>
            )}
            {!hasBalance && onOrder > 0 && (
                <div className="easytrade-coin-balance">
                    Bloqueado em ordens: {onOrder.toFixed(8)} {coin}
                </div>
            )}
            <div className="easytrade-coin-actions">
                <button 
                    className="easytrade-btn easytrade-btn-buy"
                    onClick={() => onBuy(coin)}
                >
                    Comprar
                </button>
                <button 
                    className={`easytrade-btn easytrade-btn-sell ${!hasBalance ? 'disabled' : ''}`}
                    onClick={() => hasBalance && onSell(coin)}
                    disabled={!hasBalance}
                >
                    Vender
                </button>
            </div>
        </div>
    );
}

function EasyTrade() {
    const isCustody = localStorage.getItem('isCustody') === 'true';
    const quoteAsset = isCustody ? 'USDC' : 'USDT';
    const [coins, setCoins] = useState(() =>
        isCustody
            ? ALLOWED_COINS.filter(c => !['USDT', 'USDC'].includes(c.symbol))
            : ALLOWED_COINS
    );
    const [prices, setPrices] = useState({});
    const [userBalance, setUserBalance] = useState({});
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [showBuyWizard, setShowBuyWizard] = useState(false);
    const [showSellScreen, setShowSellScreen] = useState(false);
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [usdtBrlRate, setUsdtBrlRate] = useState(0);

    // Carrega saldo do usuário
    useEffect(() => {
        loadUserBalance();
    }, []);

    useEffect(() => {
        const loadUsdtBrlRate = async () => {
            try {
                const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=USDTBRL');
                const data = await response.json();
                const rate = parseFloat(data.price || 0);
                if (rate > 0) {
                    setUsdtBrlRate(rate);
                    return;
                }
            } catch (err) {
                console.error('Erro ao carregar taxa USDT/BRL (Binance):', err);
            }

            try {
                const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=tether&vs_currencies=brl');
                const data = await response.json();
                const rate = parseFloat(data?.tether?.brl || 0);
                if (rate > 0) {
                    setUsdtBrlRate(rate);
                }
            } catch (err) {
                console.error('Erro ao carregar taxa USDT/BRL (CoinGecko):', err);
            }
        };

        loadUsdtBrlRate();
        const interval = setInterval(loadUsdtBrlRate, 15000);
        return () => clearInterval(interval);
    }, []);

    // Carrega preços iniciais via API
    useEffect(() => {
        const loadInitialPrices = async () => {
            try {
                const pricePromises = coins.map(async (coinObj) => {
                    const coin = coinObj.symbol || coinObj;
                    const symbol = isCustody
                        ? `${coin}${quoteAsset}`
                        : (SYMBOL_MAP[coin] || `${coin}USDT`);
                    if (coin === 'USDT' || coin === 'USDC' || coin === 'BUSD') {
                        return { coin, price: 1 };
                    }
                    try {
                        const response = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`);
                        const data = await response.json();
                        const price = parseFloat(data.price || 0);
                        if (price > 0) {
                            return { coin, price };
                        }
                        throw new Error('Preço inválido');
                    } catch (err) {
                        console.error(`Erro ao buscar preço de ${coin} na Binance:`, err);
                        if (coin === 'HYPE') {
                            try {
                                const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=hyperliquid&vs_currencies=usd');
                                const data = await response.json();
                                const price = parseFloat(data?.hyperliquid?.usd || 0);
                                return { coin, price };
                            } catch (cgErr) {
                                console.error(`Erro ao buscar preço de ${coin} na CoinGecko:`, cgErr);
                            }
                        }
                        return { coin, price: 0 };
                    }
                });
                const results = await Promise.all(pricePromises);
                const priceMap = {};
                results.forEach(({ coin, price }) => {
                    priceMap[coin] = price;
                });
                setPrices(priceMap);
            } catch (err) {
                console.error('Erro ao carregar preços iniciais:', err);
            }
        };
        loadInitialPrices();

        // Atualiza preços a cada 5 segundos
        const interval = setInterval(() => {
            loadInitialPrices();
        }, 5000);

        return () => clearInterval(interval);
    }, [coins]);

    async function loadUserBalance() {
        try {
            setLoading(true);
            const balance = await getBalance(false);
            const balanceObj = {};
            
            const allowedSymbols = ALLOWED_COINS.map(c => c.symbol || c);
            if (Array.isArray(balance)) {
                balance.forEach(item => {
                    const asset = item.asset || item.symbol;
                    if (allowedSymbols.includes(asset)) {
                        balanceObj[asset] = {
                            available: parseBalanceQty(item.available ?? item.availableBalance),
                            onOrder: parseBalanceQty(item.onOrder)
                        };
                    }
                });
            } else {
                Object.entries(balance).forEach(([key, value]) => {
                    if (key !== 'fiatEstimate' && allowedSymbols.includes(key)) {
                        balanceObj[key] = {
                            available: parseBalanceQty(value && (value.available ?? value.availableBalance)),
                            onOrder: parseBalanceQty(value && value.onOrder)
                        };
                    }
                });
            }
            
            setUserBalance(balanceObj);
        } catch (err) {
            console.error('Erro ao carregar saldo:', err);
            setNotification({ type: 'error', text: 'Erro ao carregar saldo da carteira.' });
        } finally {
            setLoading(false);
        }
    }

    function handleBuy(coin) {
        setSelectedCoin(coin);
        setShowBuyWizard(true);
    }

    function handleSell(coin) {
        const balance = userBalance[coin];
        const available = parseBalanceQty(balance && balance.available);
        const onOrder = parseBalanceQty(balance && balance.onOrder);
        if (available <= 0) {
            setNotification({ 
                type: 'error', 
                text: onOrder > 0
                    ? `Seu ${coin} está bloqueado em ordens abertas (${onOrder.toFixed(8)}). Cancele as ordens para vender.`
                    : `Você não possui ${coin} disponível para vender.` 
            });
            return;
        }
        setSelectedCoin(coin);
        setShowSellScreen(true);
    }

    function handleCloseWizard() {
        setShowBuyWizard(false);
        setSelectedCoin(null);
        loadUserBalance(); // Recarrega saldo após operação
    }

    function handleCloseSell() {
        setShowSellScreen(false);
        setSelectedCoin(null);
        loadUserBalance(); // Recarrega saldo após operação
    }

    if (showBuyWizard && selectedCoin) {
        return (
            <>
                <Menu />
                <BuyWizard 
                    coin={selectedCoin} 
                    price={prices[selectedCoin] || 0}
                    onClose={handleCloseWizard}
                    onSuccess={() => {
                        setNotification({ type: 'success', text: 'Compra realizada com sucesso!' });
                        handleCloseWizard();
                    }}
                />
                <Footer />
            </>
        );
    }

    if (showSellScreen && selectedCoin) {
        return (
            <>
                <Menu />
                <SellScreen 
                    coin={selectedCoin} 
                    price={prices[selectedCoin] || 0}
                    balance={userBalance[selectedCoin]}
                    onClose={handleCloseSell}
                    onSuccess={() => {
                        setNotification({ type: 'success', text: 'Venda realizada com sucesso!' });
                        handleCloseSell();
                    }}
                />
                <Footer />
            </>
        );
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="easytrade-container">
                    <div className="easytrade-header">
                        <h1>EasyTrade</h1>
                        <p className="easytrade-subtitle">Compre e venda criptomoedas de forma simples e rápida</p>
                    </div>

                    {loading ? (
                        <div className="easytrade-loading">
                            <p>Carregando...</p>
                        </div>
                    ) : (
                        <div className="easytrade-grid">
                            {coins.map(coinObj => {
                                const coin = coinObj.symbol || coinObj;
                                const coinName = coinObj.name || coin;
                                return (
                                    <CoinCard
                                        key={coin}
                                        coin={coin}
                                        coinName={coinName}
                                        price={prices[coin] || 0}
                                        usdtBrlRate={usdtBrlRate}
                                        userBalance={userBalance}
                                        onBuy={handleBuy}
                                        onSell={handleSell}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>
            <Footer />
            {notification.text && (
                <Toast
                    type={notification.type}
                    text={notification.text}
                    onClose={() => setNotification({ type: '', text: '' })}
                />
            )}
        </>
    );
}

export default EasyTrade;

